/**
 * Wraps tanstack's `useQueryClient` and insert some utils fns to improve DX
 * of `useInfiniteQuery`
 */
import { useQueryClient as useTanstackQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

const getPagesArr = (stored) => {
  if (!stored) {
    return [];
  }

  if ('pages' in stored) {
    return stored.pages;
  }

  return [stored];
};

const setInfiniteQueryData = (client) => (queryKey, updater) => {
  if (typeof updater !== 'function') {
    client.setQueryData(queryKey, updater);
    return;
  }

  client.setQueryData(queryKey, (stored) => {
    const result = updater(getPagesArr(stored));

    return { pages: result, pageParams: stored?.pageParams ?? [] };
  });
};

const getInfiniteQueryData = (client) => (queryKey, flattenKey) => {
  const data = client.getQueryData(queryKey);

  return 'pages' in data
    ? data.pages.flatMap((p) => p[flattenKey])
    : data.map((p) => p[flattenKey]);
};

const useQueryClient = () => {
  const tanstackClient = useTanstackQueryClient();

  const [client] = useState(() => ({
    fetchQuery: (...args) => tanstackClient.fetchQuery(...args),
    getInfiniteQueryData: (...args) =>
      getInfiniteQueryData(tanstackClient)(...args),
    getQueryData: (...args) => tanstackClient.getQueryData(...args),
    refetchQueries: (...args) => tanstackClient.refetchQueries(...args),
    removeQueries: (...args) => tanstackClient.removeQueries(...args),
    setInfiniteQueryData: (...args) =>
      setInfiniteQueryData(tanstackClient)(...args),
    setQueryData: (...args) => tanstackClient.setQueryData(...args),
  }));

  return client;
};

export default useQueryClient;
